import React from 'react';
import { useAdminPhonePoolPage } from './useAdminPhonePoolPage';

const statusBadge = (status: string) => {
  const styles: Record<string, string> = {
    available: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30',
    assigned: 'bg-blue-500/20 text-blue-300 border-blue-500/30',
    retired: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
  };
  return (
    <span className={`inline-flex px-2 py-0.5 text-[10px] font-semibold rounded-full border ${styles[status] || styles.retired}`}>
      {status}
    </span>
  );
};

const StatCard: React.FC<{ label: string; value: number; color: string }> = ({ label, value, color }) => (
  <div className="bg-white/5 border border-white/10 rounded-xl p-4 backdrop-blur-sm">
    <p className="text-xs text-gray-400 mb-1">{label}</p>
    <p className={`text-2xl font-bold ${color}`}>{value}</p>
  </div>
);

const AdminPhonePoolPage: React.FC = () => {
  const {
    numbers,
    stats,
    loading,
    error,
    success,
    setError,
    setSuccess,
    statusFilter,
    setStatusFilter,
    typeFilter,
    setTypeFilter,
    provisionCount,
    setProvisionCount,
    provisionCountry,
    setProvisionCountry,
    provisionType,
    setProvisionType,
    provisioning,
    handleProvision,
    handleRetire,
    handleReactivate,
    twilioStatus,
    testingTwilio,
    handleTestTwilio,
    syncingTelnyx,
    handleSyncTelnyx,
    isGateway,
  } = useAdminPhonePoolPage();

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6 mt-4">
      {/* Messages */}
      {error && (
        <div className="bg-red-500/20 border border-red-400/30 text-red-300 px-4 py-3 rounded-lg text-sm flex items-center justify-between">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="text-red-300 hover:text-white ml-2">
            &times;
          </button>
        </div>
      )}
      {success && (
        <div className="bg-emerald-500/20 border border-emerald-400/30 text-emerald-200 px-4 py-3 rounded-lg text-sm flex items-center justify-between">
          <span>{success}</span>
          <button onClick={() => setSuccess(null)} className="text-emerald-200 hover:text-white ml-2">
            &times;
          </button>
        </div>
      )}

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="Total" value={stats.total} color="text-white" />
          <StatCard label="Available" value={stats.available} color="text-emerald-400" />
          <StatCard label="Assigned" value={stats.assigned} color="text-blue-400" />
          <StatCard label="Retired" value={stats.retired} color="text-gray-400" />
        </div>
      )}

      {/* Provision Numbers */}
      <div className="bg-white/5 border border-white/10 rounded-xl p-5 backdrop-blur-sm space-y-4">
        <div className="flex items-center gap-3">
          <h3 className="text-sm font-semibold text-white">Provision Numbers</h3>
          {isGateway && (
            <span className="text-[10px] px-2 py-0.5 bg-amber-500/20 text-amber-300 border border-amber-500/30 rounded-full font-medium">
              Voice Gateway (Telnyx)
            </span>
          )}
        </div>

        {isGateway && (
          <div className="text-xs px-3 py-2 rounded-lg bg-amber-500/10 text-amber-300 border border-amber-500/20 flex items-center justify-between">
            <span>
              Auto-provisioning is not available for Telnyx numbers (document verification required).
              Purchase numbers in the Telnyx portal, and they will appear here automatically.
            </span>
            <button
              onClick={handleSyncTelnyx}
              disabled={syncingTelnyx}
              className="ml-3 shrink-0 px-3 py-1 bg-amber-500/20 text-amber-200 rounded-md text-[10px] font-medium hover:bg-amber-500/30 border border-amber-500/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {syncingTelnyx ? 'Syncing...' : 'Sync from Telnyx'}
            </button>
          </div>
        )}

        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs text-gray-400 mb-1">Count</label>
            <input
              type="number"
              min={1}
              max={10}
              value={provisionCount}
              onChange={(e) => setProvisionCount(Math.max(1, parseInt(e.target.value) || 1))}
              disabled={isGateway}
              className="w-20 px-3 py-1.5 bg-white/5 border border-white/10 rounded-lg text-xs text-white focus:outline-none focus:ring-2 focus:ring-cyan-500 disabled:opacity-30 disabled:cursor-not-allowed"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Country</label>
            <select
              value={provisionCountry}
              onChange={(e) => setProvisionCountry(e.target.value)}
              disabled={isGateway}
              className="px-3 py-1.5 bg-white/5 border border-white/10 rounded-lg text-xs text-white focus:outline-none focus:ring-2 focus:ring-cyan-500 disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <option value="AU">AU</option>
              <option value="US">US</option>
              <option value="GB">GB</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Type</label>
            <select
              value={provisionType}
              onChange={(e) => setProvisionType(e.target.value)}
              disabled={isGateway}
              className="px-3 py-1.5 bg-white/5 border border-white/10 rounded-lg text-xs text-white focus:outline-none focus:ring-2 focus:ring-cyan-500 disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <option value="mobile">Mobile</option>
              <option value="local">Local</option>
              <option value="tollfree">Toll-Free</option>
            </select>
          </div>
          <button
            onClick={handleProvision}
            disabled={provisioning || isGateway}
            className="px-4 py-1.5 bg-gradient-to-r from-cyan-600 to-blue-600 text-white rounded-lg text-xs font-medium hover:from-cyan-700 hover:to-blue-700 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
          >
            {provisioning ? 'Provisioning...' : 'Provision Numbers'}
          </button>
          <button
            onClick={handleTestTwilio}
            disabled={testingTwilio || isGateway}
            className="px-4 py-1.5 bg-white/5 text-gray-300 rounded-lg text-xs font-medium hover:bg-white/10 border border-white/20 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
          >
            {testingTwilio ? 'Testing...' : 'Test Twilio Connection'}
          </button>
        </div>

        {twilioStatus && !isGateway && (
          <div className={`text-xs px-3 py-2 rounded-lg ${twilioStatus.connected ? 'bg-emerald-500/10 text-emerald-300' : 'bg-red-500/10 text-red-300'}`}>
            {twilioStatus.connected
              ? `Connected${twilioStatus.account_name ? ` — ${twilioStatus.account_name}` : ''}`
              : `Not connected: ${twilioStatus.error || 'Unknown error'}`}
          </div>
        )}
      </div>

      {/* Filters + Table */}
      <div className="bg-white/5 border border-white/10 rounded-xl backdrop-blur-sm overflow-hidden">
        <div className="px-5 py-3 border-b border-white/10 flex flex-wrap items-center gap-3">
          <h3 className="text-sm font-semibold text-white mr-auto">Pool Numbers</h3>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-1 bg-white/5 border border-white/10 rounded-lg text-xs text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
          >
            <option value="">All Statuses</option>
            <option value="available">Available</option>
            <option value="assigned">Assigned</option>
            <option value="retired">Retired</option>
          </select>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="px-3 py-1 bg-white/5 border border-white/10 rounded-lg text-xs text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
          >
            <option value="">All Types</option>
            <option value="mobile">Mobile</option>
            <option value="local">Local</option>
            <option value="tollfree">Toll-Free</option>
          </select>
        </div>

        {numbers.length === 0 ? (
          <div className="px-5 py-10 text-center text-sm text-gray-400">
            No phone numbers in pool. Use the Provision button above to add numbers.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-white/10 text-gray-400">
                  <th className="px-4 py-2 text-left font-medium">Phone Number</th>
                  <th className="px-4 py-2 text-left font-medium">Type</th>
                  <th className="px-4 py-2 text-left font-medium">Country</th>
                  <th className="px-4 py-2 text-left font-medium">Status</th>
                  <th className="px-4 py-2 text-left font-medium">Assigned To</th>
                  <th className="px-4 py-2 text-left font-medium">serverUrl</th>
                  <th className="px-4 py-2 text-left font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {numbers.map((n) => (
                  <tr key={n.id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                    <td className="px-4 py-2 text-white font-mono">{n.phone_number}</td>
                    <td className="px-4 py-2 text-gray-300">{n.number_type}</td>
                    <td className="px-4 py-2 text-gray-300">{n.country_code}</td>
                    <td className="px-4 py-2">{statusBadge(n.status)}</td>
                    <td className="px-4 py-2 text-gray-300">{n.assigned_to_user_email || '—'}</td>
                    <td className="px-4 py-2">
                      {n.server_url_configured ? (
                        <span className="text-emerald-400">Configured</span>
                      ) : (
                        <span className="text-gray-400">Not set</span>
                      )}
                    </td>
                    <td className="px-4 py-2">
                      {n.status === 'available' && (
                        <button
                          onClick={() => handleRetire(n.id)}
                          className="text-gray-400 hover:text-red-300 text-[10px] border border-white/10 rounded px-2 py-0.5 hover:border-red-400/30 transition-colors"
                        >
                          Retire
                        </button>
                      )}
                      {n.status === 'retired' && (
                        <button
                          onClick={() => handleReactivate(n.id)}
                          className="text-gray-400 hover:text-emerald-300 text-[10px] border border-white/10 rounded px-2 py-0.5 hover:border-emerald-400/30 transition-colors"
                        >
                          Reactivate
                        </button>
                      )}
                      {n.status === 'assigned' && (
                        <span className="text-gray-400 text-[10px]">In use</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPhonePoolPage;
